import { get } from 'lodash';
import Decimal from 'decimal.js';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { daysAdded } from '../../util/dates';
import { getLodgingTax, getLodgingPricesOptions } from '../../util/listing';
import { generateTransaction } from '../../util/transaction';
import { LODGING_INFO, getPackageLodgingPrices } from '../../util/package';
import config from '../../config';
import {
  LINE_ITEM_ADULT_HUNTER,
  LINE_ITEM_ADULT_NON_HUNTER,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_LODGING_FEE,
  LINE_ITEM_LODGING_TAX,
  LINE_ITEM_YOUTH_HUNTER,
  LINE_ITEM_YOUTH_NON_HUNTER,
} from '../../util/types';

const { Money } = sdkTypes;

const INVALID_LODGING_PRICE = Symbol('invalid-lodging-price');

export const readLodgingPrice = price => {
  if (!price) {
    return null;
  }

  if (typeof price !== 'string') {
    return price;
  }

  try {
    return JSON.parse(price, sdkTypes.reviver);
  } catch (err) {
    return INVALID_LODGING_PRICE;
  }
};

const FLEXIBLE_GUEST_LABELS = {
  [LINE_ITEM_ADULT_HUNTER]: ['Adult Hunter', 'Adult Hunters'],
  [LINE_ITEM_ADULT_NON_HUNTER]: ['Adult Non-Hunter', 'Adult Non-Hunters'],
  [LINE_ITEM_YOUTH_HUNTER]: ['Youth Hunter', 'Youth Hunters'],
  [LINE_ITEM_YOUTH_NON_HUNTER]: ['Youth Non-Hunter', 'Youth Non-Hunters'],
};

export const getInquiryGuestCount = (partySize, fallback = 1) => {
  if (partySize && typeof partySize === 'object') {
    const count = parseInt(partySize.partySize, 10);
    return Number.isFinite(count) && count > 0 ? count : fallback;
  }

  const count = parseInt(partySize, 10);
  return Number.isFinite(count) && count > 0 ? count : fallback;
};

const guestLabel = (code, count) => {
  const labels = FLEXIBLE_GUEST_LABELS[code];
  if (!labels) {
    return count === 1 ? 'Guest' : 'Guests';
  }

  return count === 1 ? labels[0] : labels[1];
};

const priceTimesLabel = (intl, unitPrice, count, code, dayCount) => {
  const price = formatMoney(intl, unitPrice);
  const who = guestLabel(code, count);

  if (dayCount > 1) {
    return `${price} × ${count} ${who} × ${dayCount} days`;
  }

  return `${price} × ${count} ${who}`;
};

const buildBookingData = (listing, formValues, intl) => {
  const currentListing = ensureListing(listing);
  const listingPrice = get(currentListing, 'attributes.price', null);
  const { bookingDates, partySize, selectedPackage, lodgingAddons } = formValues || {};
  const { startDate, endDate, startLodgingDate } = bookingDates || {};

  if (!selectedPackage || !startDate || !endDate || !listingPrice) {
    return null;
  }

  const { hasLodging } = selectedPackage;
  const packageMinParty = get(selectedPackage, 'guests.min', 1);
  const guestCount = getInquiryGuestCount(partySize, packageMinParty);
  const flexiblePricingData =
    partySize && typeof partySize === 'object' ? partySize.flexibleGuestSize : null;

  const lodgingTax = selectedPackage.id ? getLodgingTax(listing, selectedPackage.id) : null;
  let optionalLodging = [];
  let includedLodging = [];
  let lodgingFees = [];

  try {
    const lodgingPricesOptions = getLodgingPricesOptions(listing, intl);
    ({ optionalLodging, includedLodging } = getPackageLodgingPrices(
      selectedPackage,
      lodgingPricesOptions
    ));

    const readFee = item => {
      if (!item?.price) {
        return null;
      }

      const lodgingPrice = readLodgingPrice(item.price);
      if (lodgingPrice === INVALID_LODGING_PRICE || !lodgingPrice) {
        throw new Error('Invalid lodging price');
      }

      return {
        price: lodgingPrice,
        type: item.type,
      };
    };

    lodgingFees = (lodgingAddons || [])
      .map(addon => readFee(optionalLodging[addon]))
      .filter(Boolean);

    if (includedLodging?.length) {
      lodgingFees = lodgingFees.concat(includedLodging.map(readFee).filter(Boolean));
    }
  } catch (err) {
    // A bad lodging price used to throw while the estimate was rendering.
    return null;
  }

  const includeLodging = lodgingAddons?.length > 0 || hasLodging === LODGING_INFO.REQUIRED;
  const packagePrice = selectedPackage.price;
  if (!packagePrice?.amount) {
    return null;
  }

  const packageFee = new Money(packagePrice.amount, packagePrice.currency || config.currency);
  const packageItem = {
    code: `line-item/${String(selectedPackage.title || 'package').replace(/\s/g, '-')}`,
    title: selectedPackage.title,
    includeFor: ['customer', 'provider'],
    unitPrice: packageFee,
    quantity: new Decimal(1),
    lineTotal: packageFee,
    reversal: false,
    guestSize: guestCount,
    activity: selectedPackage.activity,
    hasLodging: includeLodging,
    lodgingFees,
  };

  return {
    bookingData: {
      unitType: config.bookingUnitType,
      unitPrice: listingPrice,
      startDate,
      endDate,
      startLodgingDate,
      quantity: 1,
      partySize: guestCount,
      packageItem,
      lodgingTax: includeLodging && lodgingTax,
      flexiblePricing: flexiblePricingData,
    },
    guestCount,
    packageFee,
  };
};

/**
 * Same estimate the inquiry review step used to show, shaped for PricingDetails.
 * Returns null until dates are selected.
 */
export const buildInquiryPricingView = ({ listing, formValues, intl }) => {
  const built = buildBookingData(listing, formValues, intl);
  if (!built) {
    return null;
  }

  const { bookingData, guestCount, packageFee } = built;
  const tx = generateTransaction(bookingData, listing, undefined, formValues.selectedPackage);

  if (!tx?.attributes?.payinTotal?.amount && !tx?.attributes?.payoutTotal?.amount) {
    return null;
  }

  const lineItems = tx.attributes.lineItems || [];
  const items = [];
  const flexibleLines = lineItems.filter(
    item => FLEXIBLE_GUEST_LABELS[item.code] && item.lineTotal?.amount
  );

  if (flexibleLines.length) {
    flexibleLines.forEach(item => {
      const count = item.guestSize || 1;
      const dayCount = item.days || 1;
      items.push({
        label: priceTimesLabel(intl, item.unitPrice, count, item.code, dayCount),
        amount: formatMoney(intl, item.lineTotal),
        minorAmount: Number(item.lineTotal.amount),
      });
    });
  } else {
    const dayCount = daysAdded(bookingData.startDate, bookingData.endDate);
    const amount = new Money(packageFee.amount * guestCount * dayCount, packageFee.currency);
    items.push({
      label: priceTimesLabel(intl, packageFee, guestCount, 'guests', dayCount),
      amount: formatMoney(intl, amount),
      minorAmount: Number(amount.amount),
    });
  }

  const lodgingFee = lineItems.find(
    item => item.code === LINE_ITEM_LODGING_FEE && item.lineTotal?.amount
  );
  if (lodgingFee) {
    items.push({
      label: intl.formatMessage({ id: 'InquiryWizard.lodgingFee' }),
      amount: formatMoney(intl, lodgingFee.lineTotal),
      minorAmount: Number(lodgingFee.lineTotal.amount),
    });
  }

  const lodgingTax = lineItems.find(
    item => item.code === LINE_ITEM_LODGING_TAX && item.lineTotal?.amount
  );
  if (lodgingTax) {
    items.push({
      label: intl.formatMessage({ id: 'BookingBreakdown.lodgingTax' }),
      amount: formatMoney(intl, lodgingTax.lineTotal),
      minorAmount: Number(lodgingTax.lineTotal.amount),
    });
  }

  const currency =
    packageFee.currency ||
    tx.attributes.payinTotal?.currency ||
    lineItems.find(item => item.lineTotal?.currency)?.lineTotal.currency;
  let totalMinor = items.reduce((sum, item) => sum + Number(item.minorAmount || 0), 0);

  lineItems
    .filter(
      item =>
        item.code === LINE_ITEM_CUSTOMER_COMMISSION &&
        !item.reversal &&
        !item.donation &&
        item.lineTotal?.amount
    )
    .forEach(item => {
      items.push({
        label: intl.formatMessage({ id: 'InquiryWizard.bookingFee' }),
        amount: formatMoney(intl, item.lineTotal),
      });
      totalMinor += Number(item.lineTotal.amount);
    });

  return {
    items: items.map(({ minorAmount, ...item }) => item),
    // Sum of the lines above: hunter price and booking fee, plus lodging when shown.
    // The inquiry estimate does not include the conservation donation.
    totalAmount: formatMoney(intl, new Money(totalMinor, currency)),
  };
};
